/**
 * "Há quanto tempo" — a distância entre um instante e agora.
 *
 * ## Por que virou módulo
 *
 * A mesma conta estava escrita três vezes, em `Bell.jsx`, `AdminFeedback.jsx`
 * e `Admin.jsx`, cada uma com um vocabulário diferente e **o mesmo defeito**:
 * data no futuro produzia diferença negativa, e a divisão inteira jogava tudo
 * no menor balde. No sino isso virava "agora" para toda notificação.
 *
 * ## O defeito que fez isto ser reescrito
 *
 * Até 03/09 o servidor devolvia data como texto sem marcador de fuso, e o
 * `new Date()` do navegador lia como hora LOCAL — três horas à frente para
 * quem está em São Paulo. Toda notificação recém-criada ficava no futuro, a
 * subtração dava negativo, e o sino dizia "agora" por três horas seguidas.
 *
 * A causa foi corrigida no servidor (`dateStrings: false` com `timezone: 'Z'`,
 * em `backend/src/db/mysql.js`), e datas agora chegam em ISO com `Z`.
 *
 * **Mas a tolerância aqui existe de propósito**, e não é a mesma coisa que a
 * correção: um relógio local adiantado produz o mesmo sintoma, e ninguém
 * controla o relógio da máquina de quem usa. A diferença é que agora o
 * problema APARECE em vez de se esconder:
 *
 *   · até 1 minuto no futuro  → "agora". É deriva normal de relógio
 *   · além disso              → a data absoluta, porque "agora" seria mentira
 *
 * Se um dia toda a lista voltar a mostrar data em vez de "há 5 min", o defeito
 * está visível na primeira olhada — que é exatamente o que faltou da outra vez.
 */

/** Deriva de relógio que se aceita sem chamar de erro. */
const SKEW_TOLERANCE_MS = 60_000;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A conta, uma vez só. Devolve `null` quando não há data ou ela não se lê —
 * cada chamador decide o que mostrar no lugar.
 *
 * @returns {{minutes: number, hours: number, days: number, months: number,
 *            tooFarAhead: boolean} | null}
 */
function elapsed(value) {
  if (!value) return null;
  const at = new Date(value).getTime();
  if (!Number.isFinite(at)) return null;

  const ms = Date.now() - at;
  return {
    minutes: Math.floor(Math.max(0, ms) / MINUTE),
    hours: Math.floor(Math.max(0, ms) / HOUR),
    days: Math.floor(Math.max(0, ms) / DAY),
    months: Math.floor(Math.max(0, ms) / (30 * DAY)),
    tooFarAhead: ms < -SKEW_TOLERANCE_MS,
  };
}

/** `03/09/2026 15:42` — a data por extenso, para o título e para o `<time>`. */
export function exactTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * O vocabulário do sino: `agora`, `há 5 min`, `há 3 h`, `ontem`, `há 12 dias`.
 *
 * Passa de um mês, a distância deixa de ajudar — "há 47 dias" obriga quem lê a
 * fazer a conta de cabeça, e a data resolve na hora.
 */
export function relativeTime(value) {
  const e = elapsed(value);
  if (!e) return '';
  if (e.tooFarAhead) return exactTime(value);

  if (e.minutes < 1) return 'agora';
  if (e.minutes < 60) return `há ${e.minutes} min`;
  if (e.hours < 24) return `há ${e.hours} h`;
  if (e.days === 1) return 'ontem';
  if (e.days < 30) return `há ${e.days} dias`;
  return exactTime(value);
}

/**
 * Quanto tempo uma mensagem está esperando resposta. Vocabulário mais grosso
 * de propósito: o que importa na caixa da equipe é a ordem de grandeza — se
 * alguém espera há horas ou há dias —, não o minuto exato.
 */
export function waitingTime(value) {
  const e = elapsed(value);
  if (!e) return null;
  if (e.tooFarAhead) return null;

  if (e.hours < 1) return 'há menos de 1 h';
  if (e.hours < 24) return `há ${e.hours} h`;
  if (e.days === 1) return 'há 1 dia';
  return `há ${e.days} dias`;
}

/**
 * Distância em dias, para "último acesso" na lista de usuários. Aqui a
 * granularidade de minuto não diz nada: ninguém pergunta se a pessoa entrou
 * há 40 minutos, pergunta se ela entrou esta semana.
 */
export function relativeDays(value) {
  const e = elapsed(value);
  if (!e) return null;
  if (e.tooFarAhead) return null;

  if (e.days === 0) return 'hoje';
  if (e.days === 1) return 'ontem';
  if (e.days < 30) return `há ${e.days} dias`;
  return e.months === 1 ? 'há 1 mês' : `há ${e.months} meses`;
}
